// src/review/canvas/sections/MitigationActionsSection.jsx
import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Card, Stack, Text, Divider, Badge, Group } from '@mantine/core';
import { useReview } from '../../context/ReviewContext';
import EditableField from '../shared/EditableField';
import { persistControlName } from '../../../redux/slices/risksSlice';

/**
 * MitigationActionsSection - Shows mitigation actions for a specific risk
 * Pulls data from Redux controls.savedControls using riskId as key
 * Filters by modelId to prevent showing actions from other models
 * Matches ActionPlan.js rendering logic adapted for single risk
 */
function MitigationActionsSection({ risk }) {
  const { modelId, assessmentUuid } = useReview();
  const dispatch = useDispatch();

  // Get controls from Redux - keyed by riskId
  const savedControls = useSelector((state) => state.controls.savedControls || {});
  const savedEvaluations = useSelector((state) => state.controls.savedControlEvaluations || {});

  // Get controls for this specific risk, filtered by modelId
  const riskControls = useMemo(() => {
    const controlsArray = savedControls[risk.riskId] || [];
    // IMPORTANT: Filter by modelId to prevent showing controls from other models
    return controlsArray.filter(ctrl => ctrl.modelId === modelId);
  }, [savedControls, risk.riskId, modelId]);

  // Handler for saving control name edits
  const handleSaveControlName = useCallback((ctrl) => {
    return async (newValue, oldValue) => {
      if (newValue === oldValue) return;

      await dispatch(persistControlName({
        modelId,
        assessmentUuid,
        controlId: ctrl.controlId,
        controlName: newValue,
        riskIds: ctrl.riskIds || [risk.riskId]
      }));
    };
  }, [dispatch, modelId, assessmentUuid, risk.riskId]);

  return (
    <Card withBorder p="md">
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="md" fw={700}>Mitigation Actions</Text>
          <Badge size="md" variant="light">
            {riskControls.length} {riskControls.length === 1 ? 'Action' : 'Actions'}
          </Badge>
        </Group>

        {riskControls.length > 0 ? (
          <Stack gap="lg">
            {riskControls.map((ctrl, index) => {
              // Get evaluation data for this control
              const evalData = savedEvaluations[risk.riskId]?.[ctrl.controlId]?.[0] || {};
              const isEvaluated = evalData.control_recurrence || evalData.executed_next || evalData.self_evaluation;

              return (
                <div key={ctrl.controlId || index}>
                  {index > 0 && <Divider />}

                  <Stack gap="sm">
                    <Group justify="space-between">
                      <Text size="sm" fw={600}>Action {index + 1}</Text>
                      {isEvaluated && (
                        <Badge size="xs" color="green">Evaluated</Badge>
                      )}
                    </Group>

                    <EditableField
                      label="Action Description"
                      value={ctrl.name || 'No description'}
                      itemType="control"
                      itemId={ctrl.controlId}
                      fieldName="name"
                      minRows={2}
                      placeholder="Describe the mitigation action..."
                      onSave={handleSaveControlName(ctrl)}
                    />

                    {evalData.control_recurrence && (
                      <EditableField
                        label="Recurrence"
                        value={evalData.control_recurrence}
                        itemType="control_evaluation"
                        itemId={ctrl.controlId}
                        fieldName="control_recurrence"
                        readOnly
                      />
                    )}

                    {evalData.executed_next && (
                      <EditableField
                        label="Execution Timeline"
                        value={evalData.executed_next}
                        itemType="control_evaluation"
                        itemId={ctrl.controlId}
                        fieldName="executed_next"
                        readOnly
                      />
                    )}

                    {evalData.self_evaluation && (
                      <EditableField
                        label="Effectiveness Assessment"
                        value={evalData.self_evaluation}
                        itemType="control_evaluation"
                        itemId={ctrl.controlId}
                        fieldName="self_evaluation"
                        readOnly
                      />
                    )}
                  </Stack>
                </div>
              );
            })}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">No mitigation actions defined for this risk.</Text>
        )}
      </Stack>
    </Card>
  );
}

MitigationActionsSection.propTypes = {
  risk: PropTypes.shape({
    riskId: PropTypes.string.isRequired,
  }).isRequired,
};

export default MitigationActionsSection;
